import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { QuoteAttachmentApiService } from '../../services/quote-attachment-api.service';
import {
  QUOTE_ATTACHMENT_ACCEPT,
  QuoteAttachmentDownloadEntry,
  QuoteAttachmentResponse,
  formatAttachmentSize,
  quoteAttachmentDownloadViaLabel,
  validateQuoteAttachmentFile
} from '../../models/quote-attachment.model';
import { QuoteAttachmentIconComponent } from './quote-attachment-icon.component';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../../shared/services/datetime.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';

/**
 * Tarjeta "Documentos adjuntos" de una cotización: listar, subir (máx 10 MB, MIME
 * restringido), descargar (tracking via=CRM), eliminar y ver el historial de descargas.
 *
 * Dos modos:
 * - default (detalle): tarjeta completa con badge de descargas + historial expandible.
 * - compact (form de edición): tarjeta colapsable con lista simple (nombre + descargar + eliminar).
 */
@Component({
  selector: 'app-quote-attachments',
  standalone: true,
  imports: [CommonModule, QuoteAttachmentIconComponent],
  templateUrl: './quote-attachments.component.html'
})
export class QuoteAttachmentsComponent implements OnChanges {
  @Input() quoteId: number | null = null;
  /** Variante compacta/colapsable para el form de edición. */
  @Input() compact = false;

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  readonly accept = QUOTE_ATTACHMENT_ACCEPT;

  attachments: QuoteAttachmentResponse[] = [];
  loading = false;
  loadError = '';

  /** Solo modo compact: la tarjeta inicia colapsada. */
  collapsed = true;

  uploading = false;
  uploadProgress: number | null = null;
  uploadingFileName = '';
  uploadError = '';

  deletingIds = new Set<number>();
  downloadingIds = new Set<number>();

  // Historial de descargas (expandible inline, solo modo default)
  expandedHistoryId: number | null = null;
  historyLoading = false;
  history: QuoteAttachmentDownloadEntry[] = [];
  historyError = '';

  constructor(
    private attachmentApi: QuoteAttachmentApiService,
    private authService: AuthService,
    private datetimeService: DatetimeService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteId']) {
      this.resetState();
      if (this.quoteId) {
        this.cargar();
      }
    }
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private resetState(): void {
    this.attachments = [];
    this.loadError = '';
    this.uploadError = '';
    this.uploading = false;
    this.uploadProgress = null;
    this.uploadingFileName = '';
    this.expandedHistoryId = null;
    this.history = [];
    this.historyError = '';
    this.deletingIds.clear();
    this.downloadingIds.clear();
  }

  private cargar(): void {
    if (!this.quoteId) return;
    this.loading = true;
    this.loadError = '';
    this.attachmentApi.getAttachments(this.quoteId, this.businessId).subscribe({
      next: (attachments) => {
        this.attachments = attachments;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err.error?.message || 'No se pudieron cargar los documentos adjuntos.';
      }
    });
  }

  // ---- Subida ----

  abrirSelector(): void {
    if (this.uploading) return;
    this.uploadError = '';
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = ''; // permite volver a elegir el mismo archivo
    if (!file || !this.quoteId) return;

    // Validación client-side (10 MB + tipo permitido) antes de tocar la red.
    const validationError = validateQuoteAttachmentFile(file);
    if (validationError) {
      this.uploadError = validationError;
      return;
    }

    this.uploadError = '';
    this.uploading = true;
    this.uploadProgress = 0;
    this.uploadingFileName = file.name;

    this.attachmentApi.uploadAttachment(this.quoteId, this.businessId, file).subscribe({
      next: (httpEvent) => {
        if (httpEvent.type === HttpEventType.UploadProgress && httpEvent.total) {
          this.uploadProgress = Math.round((httpEvent.loaded / httpEvent.total) * 100);
        } else if (httpEvent.type === HttpEventType.Response && httpEvent.body) {
          this.uploading = false;
          this.uploadProgress = null;
          this.uploadingFileName = '';
          this.attachments = [httpEvent.body, ...this.attachments];
          this.toast.success({ title: 'Documento subido', message: `"${httpEvent.body.fileName}" se adjuntó a la cotización.` });
        }
      },
      error: (err) => {
        this.uploading = false;
        this.uploadProgress = null;
        this.uploadingFileName = '';
        // 400: tipo/tamaño rechazado por el backend — mostrar su message tal cual.
        const msg = err.error?.message || 'No se pudo subir el documento. Inténtalo de nuevo.';
        this.uploadError = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  // ---- Descarga (registra tracking via=CRM) ----

  descargar(att: QuoteAttachmentResponse): void {
    if (!this.quoteId || this.downloadingIds.has(att.id)) return;
    this.downloadingIds.add(att.id);
    this.attachmentApi.downloadAttachment(this.quoteId, att.id, this.businessId).subscribe({
      next: (blob) => {
        this.downloadingIds.delete(att.id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = att.fileName;
        a.click();
        window.URL.revokeObjectURL(url);
        // El backend registró la descarga CRM: reflejarlo sin recargar toda la lista.
        att.downloadCount = (att.downloadCount || 0) + 1;
        att.lastDownloadedAt = new Date().toISOString();
        if (this.expandedHistoryId === att.id) {
          this.cargarHistorial(att);
        }
      },
      error: (err) => {
        this.downloadingIds.delete(att.id);
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo descargar el documento.' });
      }
    });
  }

  // ---- Eliminación ----

  async eliminar(att: QuoteAttachmentResponse): Promise<void> {
    if (!this.quoteId || this.deletingIds.has(att.id)) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar documento',
      message: `¿Eliminar "${att.fileName}" de esta cotización? El cliente ya no podrá descargarlo desde el portal.`
    });
    if (!ok) return;

    this.deletingIds.add(att.id);
    this.attachmentApi.deleteAttachment(this.quoteId, att.id, this.businessId).subscribe({
      next: () => {
        this.deletingIds.delete(att.id);
        this.attachments = this.attachments.filter(a => a.id !== att.id);
        if (this.expandedHistoryId === att.id) {
          this.expandedHistoryId = null;
          this.history = [];
        }
        this.toast.deleted('Adjunto');
      },
      error: (err) => {
        this.deletingIds.delete(att.id);
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo eliminar el documento.' });
      }
    });
  }

  // ---- Historial de descargas (expandible inline) ----

  toggleHistorial(att: QuoteAttachmentResponse): void {
    if (this.expandedHistoryId === att.id) {
      this.expandedHistoryId = null;
      this.history = [];
      this.historyError = '';
      return;
    }
    if (!att.downloadCount) return;
    this.expandedHistoryId = att.id;
    this.cargarHistorial(att);
  }

  private cargarHistorial(att: QuoteAttachmentResponse): void {
    if (!this.quoteId) return;
    this.historyLoading = true;
    this.history = [];
    this.historyError = '';
    this.attachmentApi.getAttachmentDownloads(this.quoteId, att.id, this.businessId).subscribe({
      next: (entries) => {
        // Protege contra respuestas tardías si el usuario ya expandió otro adjunto.
        if (this.expandedHistoryId !== att.id) return;
        this.history = entries;
        this.historyLoading = false;
      },
      error: (err) => {
        if (this.expandedHistoryId !== att.id) return;
        this.historyLoading = false;
        this.historyError = err.error?.message || 'No se pudo cargar el historial de descargas.';
      }
    });
  }

  // ---- Helpers de presentación ----

  isDownloading(att: QuoteAttachmentResponse): boolean {
    return this.downloadingIds.has(att.id);
  }

  isDeleting(att: QuoteAttachmentResponse): boolean {
    return this.deletingIds.has(att.id);
  }

  formatSize(bytes?: number | null): string {
    return formatAttachmentSize(bytes);
  }

  viaLabel(entry: QuoteAttachmentDownloadEntry): string {
    return quoteAttachmentDownloadViaLabel(entry);
  }

  downloadCountLabel(att: QuoteAttachmentResponse): string {
    const n = att.downloadCount || 0;
    return n === 1 ? '1 descarga' : `${n} descargas`;
  }

  formatDateTime(iso?: string | null): string {
    return iso ? this.datetimeService.formatDateTimeShort(iso) : '—';
  }

  formatDate(iso?: string | null): string {
    return iso ? this.datetimeService.formatDateShort(iso) : '—';
  }
}
